import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';

interface CreateSSHConnectionData {
    tenantId: string;
    name: string;
    description?: string;
    repositoryUrl: string;
    keyType?: 'rsa' | 'ed25519';
    connectionType?: 'repository' | 'organization' | 'user';
}

interface CreatePATConnectionData {
    tenantId: string;
    name: string;
    description?: string;
    repositoryUrl: string;
    personalAccessToken: string;
    connectionType?: 'repository' | 'organization' | 'user';
}

interface UpdateGitHubConnectionData {
    connectionId: string;
    tenantId: string;
    name: string;
    description?: string;
    repositoryUrl: string;
    accessMethod: 'ssh' | 'pat';
    keyType?: 'rsa' | 'ed25519';
    personalAccessToken?: string;
    connectionType?: 'repository' | 'organization' | 'user';
}



export const useCreateSSHConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (data: CreateSSHConnectionData) => {
            const response = await apiClient.post('/configurations/connections/github/ssh', data);
            
            if (!response.ok) {
                throw new Error(`Failed to create SSH connection: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['githubSshConfigs'] });
        }
    });
};

export const useCreatePATConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (data: CreatePATConnectionData) => {
            const response = await apiClient.post('/configurations/connections/github/pat', data);
            
            if (!response.ok) {
                throw new Error(`Failed to create PAT connection: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['githubSshConfigs'] });
        }
    });
};

export const useUpdateGitHubConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (data: UpdateGitHubConnectionData) => {
            const { connectionId, ...updateData } = data;
            const encodedConnectionId = encodeURIComponent(connectionId);
            const response = await apiClient.put(`/configurations/connections/github/${encodedConnectionId}`, updateData);
            
            if (!response.ok) {
                throw new Error(`Failed to update GitHub connection: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['githubSshConfigs'] });
        }
    });
};

export const useDeleteGitHubConnection = () => {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (connectionId: string) => {
            const encodedConnectionId = encodeURIComponent(connectionId);
            const response = await apiClient.delete(`/configurations/connections/github/${encodedConnectionId}`);
            
            if (!response.ok) {
                throw new Error(`Failed to delete GitHub connection: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['githubSshConfigs'] });
        }
    });
};

export const useGetGitHubConnectionPublicKey = (connectionId: string) => {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async () => {
            const encodedConnectionId = encodeURIComponent(connectionId);
            const response = await apiClient.get(`/configurations/connections/github/${encodedConnectionId}/publickey`);
            
            if (!response.ok) {
                throw new Error(`Failed to get public key: ${response.statusText}`);
            }
            
            return response.json();
        }
    });
};